import { createColumnHelper } from '@tanstack/react-table';
import styles from './PriceSchemeOptionGroupOverrides.module.scss';
import { Checkbox, Typography, Info, Popover, Tooltip, AttentionIcon } from '@weareopenr/component-library';
import { Controller, useFormState, useWatch } from 'react-hook-form';
import { TOptionGroupRow, TTableCol } from './types';
import classes from 'classnames/bind';
import { StatusChip } from '../StatusChip/StatusChip';
import { capitalise } from 'src/helpers/capitalise';
import PriceInputBase from '../PriceInputBase/PriceInputBase';
import { StandaloneDepth } from './utils';
import { ChannelAvatarTooltip } from '../ChannelAvatarTooltip';

const cx = classes.bind(styles);
const columnHelper = createColumnHelper<TOptionGroupRow>();

const validatePriceInput = (v: string) => !v || !Number.isNaN(Number(v));

enum Depth {
  Item = 0,
  ModifierGroup = 1,
  Modifier = 2,
}

export const optionGroupCols = ({ control, setValue, missingDefaultPriceModifierIds }: TTableCol) => [
  columnHelper.accessor(({ type, name }) => ({ type, name }), {
    id: 'name-cell',
    header: 'Name',
    size: 300,
    cell({ row, getValue }) {
      const { depth } = row;
      const value = getValue();
      const className = depth === Depth.Modifier ? styles['row-child-margin'] : undefined;
      return (
        <div className={styles['cell-name']}>
          <Typography weight={depth === Depth.Item ? 'bold' : 'regular'} className={className}>
            {value.name}
          </Typography>
          {value.type && <StatusChip entity="option-group" status={capitalise(value.type)} />}
        </div>
      );
    },
  }),
  columnHelper.accessor(
    (row) => {
      if (!row.ranges || row.ranges.length === 0) return '';
      return row.ranges.map((range) => range.name).join(', ');
    },
    {
      id: 'ranges',
      header: 'Ranges',
      size: 200,
    },
  ),
  columnHelper.accessor('modifier_id', {
    id: 'inherit-price-cell',
    header: InheritPriceCellHeader,
    size: 80,
    cell: ({ row, getValue }) => {
      const { depth } = row;
      const modifierId = getValue();

      return depth === Depth.Modifier ? (
        <div className={styles['cell-tooltip']}>
          <Controller
            name={`${modifierId}.inherit_price`}
            control={control}
            render={({ field, formState }) => (
              <Checkbox
                name={field.name}
                disabled={formState.isSubmitting}
                checked={Boolean(field.value)}
                onChange={(event) => {
                  setValue(`${modifierId}.value_with_tax`, null, { shouldValidate: true, shouldDirty: true });
                  field.onChange(event);
                }}
                ariaLabel="Use inherited price"
                spacing="small"
                data-testid={`modifiers-use-inherit-price`}
              />
            )}
          />

          <MissingDefaultPriceWarning
            control={control}
            modifierId={modifierId}
            missingDefaultPriceModifierIds={missingDefaultPriceModifierIds}
          />
        </div>
      ) : null;
    },
  }),
  columnHelper.accessor('modifier_id', {
    id: 'override-price-cell',
    header: 'Override Price',
    size: 80,
    cell: ({ row, getValue }) => {
      const { depth } = row;
      const modifierId = getValue();

      return depth === Depth.Modifier ? <ConditionalPriceOverride control={control} modifierId={modifierId} /> : null;
    },
  }),
  columnHelper.accessor('publish_channels', {
    id: 'publish-channel-cell',
    header: 'Publish Channels',
    size: 80,
    cell: ({ row, getValue }) => {
      if (row.depth !== Depth.ModifierGroup) {
        return null;
      }

      const channels = getValue();

      return <ChannelAvatarTooltip externalSystems={channels} />;
    },
  }),
];

const InheritPriceCellHeader = () => {
  return (
    <div className={styles['inherit-price-header']}>
      <Typography weight="bold">Inherit Prices</Typography>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" aria-label="Inherit prices information">
            <Info />
          </button>
        </Popover.Trigger>
        <Popover.Content side="bottom" align="center" sideOffset={4}>
          <div className={cx('tooltip', 'inherit-prices-header-tooltip')}>
            <Typography colour="secondary">
              Use product pricing from the price scheme, including any channel price bands and automation rules.
            </Typography>
            <Typography colour="secondary" weight="bold">
              Uncheck this if you want to set your own custom price.
            </Typography>
          </div>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
};

const standaloneColumnHelper = createColumnHelper<TOptionGroupRow>();

export const standaloneOptionGroupCols = ({ control, setValue, missingDefaultPriceModifierIds }: TTableCol) => [
  standaloneColumnHelper.accessor(({ type, name, ranges }) => ({ type, name, ranges }), {
    id: 'name-cell',
    header: 'Option Group Name',
    size: 350,
    cell({ row, getValue }) {
      const { depth } = row;
      const value = getValue();
      const rangeNames = value.ranges?.map((r) => r.name) ?? [];

      if (depth === StandaloneDepth.Group) {
        return (
          <div className={styles['cell-name']}>
            <div className={styles['cell-with-subtitle']}>
              <Typography weight="bold">{value.name}</Typography>
              {rangeNames.length > 0 && (
                <span className={styles['cell-subtitle']} title={rangeNames.join(', ')}>
                  {rangeNames.join(', ')}
                </span>
              )}
            </div>
            {value.type && (
              <StatusChip
                entity="option-group"
                status={capitalise(value.type)}
                label={`${capitalise(value.type)} Group`}
              />
            )}
          </div>
        );
      }

      return <Typography className={styles['row-child-margin']}>{value.name}</Typography>;
    },
  }),
  standaloneColumnHelper.accessor('modifier_id', {
    id: 'inherit-price-cell',
    header: InheritPriceCellHeader,
    size: 80,
    cell: ({ row, getValue }) => {
      if (row.depth !== StandaloneDepth.Option) return null;
      const modifierId = getValue();

      return (
        <div className={styles['cell-tooltip']}>
          <Controller
            name={`${modifierId}.inherit_price`}
            control={control}
            render={({ field, formState }) => (
              <Checkbox
                name={field.name}
                disabled={formState.isSubmitting}
                checked={Boolean(field.value)}
                onChange={(event) => {
                  setValue(`${modifierId}.value_with_tax`, null, { shouldValidate: true, shouldDirty: true });
                  field.onChange(event);
                }}
                ariaLabel="Use inherited price"
                spacing="small"
                data-testid="modifiers-use-inherit-price"
              />
            )}
          />

          <MissingDefaultPriceWarning
            control={control}
            modifierId={modifierId}
            missingDefaultPriceModifierIds={missingDefaultPriceModifierIds}
          />
        </div>
      );
    },
  }),
  standaloneColumnHelper.accessor('modifier_id', {
    id: 'override-price-cell',
    header: 'Override Price',
    size: 80,
    cell: ({ row, getValue }) => {
      if (row.depth !== StandaloneDepth.Option) return null;
      const modifierId = getValue();

      return <ConditionalPriceOverride control={control} modifierId={modifierId} />;
    },
  }),
  standaloneColumnHelper.accessor('publish_channels', {
    id: 'publish-channel-cell',
    header: 'Publish Channels',
    size: 80,
    cell: ({ row, getValue }) => {
      if (row.depth !== StandaloneDepth.Group) return null;
      const channels = getValue();
      if (!channels?.length) return null;

      return <ChannelAvatarTooltip externalSystems={channels} />;
    },
  }),
];

const MissingDefaultPriceWarning = ({ control, modifierId, missingDefaultPriceModifierIds }) => {
  const isMissingDefaultPrice = missingDefaultPriceModifierIds?.has(modifierId);
  const value = useWatch({ control, name: `${modifierId}.value_with_tax` });
  const hasLocalValue = value !== null && value !== undefined && value !== '';

  if (!isMissingDefaultPrice) return null;

  return (
    <div className={styles['tooltip-icon-slot']}>
      <Tooltip text="Default price is missing. Please ensure there is a default price before publishing with this price scheme.">
        <AttentionIcon className={cx('error-icon', { 'error-icon-resolved': hasLocalValue })} />
      </Tooltip>
    </div>
  );
};

const ConditionalPriceOverride = ({ control, modifierId }) => {
  const { isSubmitting } = useFormState({ control });
  const inherited = useWatch({ control, name: `${modifierId}.inherit_price` });
  const inputName = inherited ? `${modifierId}.not_in_use.value_with_tax` : `${modifierId}.value_with_tax`;

  const disabled = inherited || isSubmitting;

  return (
    <Controller
      name={inputName}
      control={control}
      rules={{
        validate: validatePriceInput,
      }}
      render={({ field, fieldState }) => (
        <PriceInputBase
          id={inputName}
          dirty={fieldState.isDirty}
          error={!!fieldState.error || (!inherited && !field.value && field.value !== 0)}
          className={cx('input', {
            'cell-input-left-align': disabled && !isSubmitting,
          })}
          disabled={disabled}
          autoFocus={false}
          {...field}
          ref={field.ref}
          value={inherited ? '-' : field.value ?? ''}
        />
      )}
    />
  );
};
